import { useState } from "react";
import { Link } from "react-router-dom";
import { format, subDays } from "date-fns";
import { useApi } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { BarList, Panel, Split, StatCard, TrafficChart } from "@/pages/admin/AnalyticsWidgets";

const d = (x) => format(x, "yyyy-MM-dd");
const RANGES = [["today", "Today", 0], ["7d", "7 days", 6], ["30d", "30 days", 29], ["12m", "12 months", 364]];
const flag = (code) => (code ? String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "🌐");

const RangePicker = ({ range, setRange, custom, setCustom }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="analytics-range">
    {RANGES.map(([id, label]) => (
      <button key={id} onClick={() => setRange(id)} data-testid={`analytics-range-${id}`}
        className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${range === id ? "bg-forest text-white" : "border border-clay bg-white text-ink/75 hover:bg-linen"}`}>{label}</button>
    ))}
    <button onClick={() => setRange("custom")} data-testid="analytics-range-custom"
      className={`rounded-full px-4 py-2 text-sm font-bold ${range === "custom" ? "bg-forest text-white" : "border border-clay bg-white text-ink/75 hover:bg-linen"}`}>Custom</button>
    {range === "custom" && (
      <div className="flex items-center gap-2">
        <Input type="date" value={custom.start} max={custom.end} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="w-40" data-testid="analytics-custom-start" />
        <span className="text-moss">to</span>
        <Input type="date" value={custom.end} min={custom.start} max={d(new Date())} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="w-40" data-testid="analytics-custom-end" />
      </div>
    )}
  </div>
);

const ListingTable = ({ rows }) => (
  <div className="overflow-x-auto" data-testid="analytics-listings">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-xs uppercase tracking-[0.15em] text-moss"><th className="py-2">Listing</th><th>Type</th><th className="text-right">Visitors</th><th className="text-right">Views</th></tr></thead>
      <tbody className="divide-y divide-clay">
        {rows.map((l) => (
          <tr key={l.path} data-testid={`analytics-listing-${l.slug}`}>
            <td className="py-2.5 pr-3 font-semibold text-ink"><Link to={l.path} target="_blank" className="hover:underline">{l.title}</Link>{!l.published && <span className="ml-2 text-xs text-moss">(hidden)</span>}</td>
            <td className="text-moss">{l.kind === "rent" ? "Place to stay" : "For sale"}</td>
            <td className="text-right font-bold text-forest">{l.visitors}</td>
            <td className="text-right text-moss">{l.views}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminAnalytics() {
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ start: d(subDays(new Date(), 13)), end: d(new Date()) });
  const days = RANGES.find((r) => r[0] === range)?.[2];
  const start = range === "custom" ? custom.start : d(subDays(new Date(), days));
  const end = range === "custom" ? custom.end : d(new Date());
  const { data, loading } = useApi(`/admin/analytics?start=${start}&end=${end}`, null);
  const [kind, setKind] = useState("sale");
  return (
    <div data-testid="admin-analytics">
      <AdminHeader title="Analytics" />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[["today", "Today"], ["week", "Last 7 days"], ["month", "Last 30 days"], ["year", "Last 12 months"]].map(([k, l]) => <StatCard key={k} label={l} data={data?.summary?.[k]} testId={`analytics-summary-${k}`} />)}
      </div>
      <div className="mt-10 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <RangePicker range={range} setRange={setRange} custom={custom} setCustom={setCustom} />
        <p className="text-sm font-semibold text-moss" data-testid="analytics-range-totals">{loading ? "Loading…" : `${data?.totals.visitors ?? 0} visitors · ${data?.totals.views ?? 0} page views`}</p>
      </div>
      {data && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title={`Visitors per ${data.range.bucket}`} className="lg:col-span-2" testId="analytics-traffic"><TrafficChart series={data.series} /></Panel>
          <Panel title="Countries" testId="analytics-countries-panel"><BarList rows={data.countries} label={(r) => `${flag(r.code)}  ${r.name}`} testId="analytics-countries" /></Panel>
          <Panel title="Cities" testId="analytics-cities-panel"><BarList rows={data.cities} label={(r) => r.city || "Unknown city"} sub={(r) => [r.region, r.country].filter(Boolean).join(", ")} testId="analytics-cities" /></Panel>
          <Panel title="Views per listing" className="lg:col-span-2" testId="analytics-listings-panel">
            <div className="mb-3 flex gap-2">
              {[["sale", "Homes & Land"], ["rent", "Places to Stay"]].map(([k, l]) => (
                <button key={k} onClick={() => setKind(k)} data-testid={`analytics-listings-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${kind === k ? "bg-forest text-white" : "border border-clay text-ink/75"}`}>{l}</button>
              ))}
            </div>
            <ListingTable rows={data.listings.filter((l) => l.kind === kind)} />
          </Panel>
          <Panel title="Top pages" testId="analytics-pages-panel"><BarList rows={data.pages} label={(r) => r.page} testId="analytics-pages" /></Panel>
          <Panel title="Where visitors came from" testId="analytics-sources-panel"><BarList rows={data.sources} label={(r) => r.name} testId="analytics-sources" /></Panel>
          <Panel title="Phone vs computer" testId="analytics-devices-panel"><Split rows={data.devices} testId="analytics-devices" /></Panel>
          <Panel title="Language" testId="analytics-langs-panel"><Split rows={data.langs} names={{ en: "English", es: "Español" }} testId="analytics-langs" /></Panel>
        </div>
      )}
      <p className="mt-8 text-xs text-moss">Times in Nicaragua time. Your own visits while logged in and bots are not counted. <a href="https://db-ip.com" target="_blank" rel="noreferrer" className="underline">IP Geolocation by DB-IP</a></p>
    </div>
  );
}
